import {CategoryType, PaymentStatusType} from "../../api/Constants";
import {clampToCents} from "../../util/unitutils";
import {toTimeString} from "../../util/timeutils";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import {fixedToDisplayUnit} from "../../util/unitutils";

export const getInvoiceTotals = (invoiceItems, payments) => {
    let subtotal;
    let discountTotal = 0;

    let sub = 0;
    let numUndefined = 0;
    let discountSum = 0;
    let costSum = 0;
    let paymentSum = 0;

    // check for empty
    if (Object.keys(invoiceItems).length !== 0) {
        for (const i of Object.values(invoiceItems)) {
            const displayQuantity = fixedToDisplayUnit(i.quantity, i.item.unit);
            const displayStartQuantity = fixedToDisplayUnit(i.item.startQuantity, i.item.unit);
            if ((i.item.linePrice === undefined) || (i.item.linePrice === null)) {
                numUndefined++;
            } else {
                sub += i.item.linePrice;
            }
            discountSum += i.item.lineDiscount
            if (i.item.category === CategoryType.Wholesale) {
                costSum += i.item.bulkCost * displayQuantity;
            } else {
                costSum += i.item.cost * displayQuantity / displayStartQuantity
            }
        }

        if ((numUndefined === 0) && (Object.keys(invoiceItems).length > 0)) {
            subtotal = sub;
        } else if (numUndefined > 0) {
            subtotal = undefined;
        }

        discountTotal = discountSum;
    }

    const hasPayments = (payments && payments.length > 0);
    if (hasPayments) {
        payments.forEach(p => {
            paymentSum += parseFloat(p.amount);
        })
        paymentSum = parseFloat(paymentSum.toFixed(2));
    }

    let profit, profitMargin, amountDue;
    if (subtotal !== undefined) {
        profit = subtotal - discountTotal - costSum;
        profitMargin = (profit * 100 / costSum).toFixed(2)

        amountDue = subtotal - discountTotal - paymentSum;
        amountDue = amountDue.toFixed(2);
    }

    return {
        subtotal, discountTotal, paymentSum, costSum, profit, profitMargin, amountDue
    }
}

export const openPdfPrintWindow = ({invoice, buyer,
    invoiceLines, invoiceItems, payments, returns}) => {

    const win = window.open('', '_blank');

    const docContent = [
        {text: `${invoice.invoiceNumber}`, style: 'header'},
        "\n\n",
        {text: 'Buyer', style: 'header'},
        `${buyer.firstname} ${buyer.lastname}`,
        `${buyer.company}`,
        `${buyer.license}`,
    ];

    const {addressId, streetAddress, streetAddressLine2, city, state, postalCode, country,
        emailId, email, phoneId, phoneNumber} = buyer;
    if (addressId) {
        // add the address
        docContent.push(`${streetAddress}`);
        if (streetAddressLine2) {
            docContent.push(`${streetAddressLine2}`)
        }
        docContent.push(`${city}, ${state} ${postalCode}`);
        if (country) {
            docContent.push(`${country}`);
        }
    }
    if (emailId) {
        docContent.push(`${email}`);
    }
    if (phoneId) {
        docContent.push(`${phoneNumber}`);
    }

    // invoice blocks
    docContent.push("\n\n");
    docContent.push({text: 'Invoice Items', style: 'header'});
    const lineContent = [
        ['Name', 'Description', 'Sku', 'Quantity', 'Line Price', 'Unit Price', 'Discount', 'Cost', 'Profit'],
    ];
    invoiceLines.forEach(l => {
        const unitCost = (l.cost / l.startQuantity).toFixed(2);
        const totalCost = (l.quantity * unitCost).toFixed(2);
        const profit = ((l.linePrice - l.lineDiscount) - totalCost).toFixed(2);
        lineContent.push([
            l.name,
            l.description,
            l.sku,
            l.quantity,
            `$ ${l.linePrice}`,
            `$ ${clampToCents(l.linePrice / l.quantity)}`,
            `$ ${l.discount}`,
            `$ ${totalCost}\n$ ${unitCost} / each`,
            `$ ${profit}`
        ]);
    })
    docContent.push({
        style: 'tableExample',
        table: {
            body: lineContent,
        }
    });

    // payment blocks
    if (payments.length > 0) {
        docContent.push("\n\n");
        docContent.push({text: 'Payments', style: 'header'});
        const paymentLines = [
            ['Payment Amount', 'Recorded By', 'Date', 'Account', 'Notes', 'Status'],
        ];
        payments.forEach(p => {
            const {accountName} = p;
            paymentLines.push([
                `$ ${p.amount}`,
                p.username,
                toTimeString(new Date(p.timestamp)),
                accountName,
                p.notes,
                PaymentStatusType.Label[p.state]
            ])
        });

        docContent.push({
            style: 'tableExample',
            table: {
                body: paymentLines,
            }
        });
    }

    // invoice totals section
    docContent.push("\n\n");
    docContent.push({text: 'Totals', style: 'header'});
    const totals = getInvoiceTotals(invoiceItems, payments);
    const {subtotal, discountTotal, paymentSum, costSum, profit, profitMargin, amountDue} = totals;

    docContent.push({
        style: 'tableExample',
        table: {
            body: [
                ['Subtotal', `$ ${subtotal}`],
                ['Discount', `$ ${discountTotal}`],
                ['Total Due', `$ ${(subtotal - discountTotal)}`],
                ['Paid', `$ ${paymentSum}`],
                ['Remaining Balance', `$ ${amountDue}`],
                ['Cost of Goods', `$ ${costSum}`],
                ['Profit', `$ ${profit}`],
                ['Profit Margin', `${profitMargin} %`],
            ],
        }
    });

    // returns section
    if (returns.length > 0) {
        docContent.push("\n\n");
        docContent.push({text: 'Returns', style: 'header'});

        const returnLines = [
            ['Invoice', 'Processed By', 'Date', 'Item', 'Quantity', 'Amount'],
        ];
        returns.forEach(r => {
            returnLines.push([
                r.invoiceNumber,
                r.username,
                toTimeString(new Date(r.timestamp)),
                r.name,
                r.quantity,
                `$ ${r.refundAmount}`
            ])
        });

        docContent.push({
            style: 'tableExample',
            table: {
                body: returnLines,
            }
        });
    }

    // final doc definition
    const docDefinition = {
        content: docContent,
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 16,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableExample: {
                fontSize: 8,
                margin: [0, 5, 0, 15]
            },
            tableHeader: {
                bold: true,
                fontSize: 13,
                color: 'black'
            }
        },
    };

    pdfMake.createPdf(docDefinition).open({}, win);
}